/**
 * Shared cohort selector for the refill feature (epic PROD-1264).
 *
 * "Concierge care" = whether the member gets their GLP-1 through 9am. Driven by the
 * `care.concierge-care` profile attribute (`ProfileAttributesKeys.conciergeCare`), fetched via
 * `getMyProfileAttributeValues`. Read from the profile-attribute endpoint rather than the raw
 * `/preferences` value: `/preferences` only reflects a value once explicitly written for the
 * member, while the profile-attribute endpoint returns the member's effective concierge-care
 * status (e.g. defaulted from their employer/payer) even when no such preference was ever
 * written (see BACK-5641 — members were falling back to "No" instead of their employer's
 * default). Centralized here so the refill hub, the home card, the medication card, and
 * NotificationCard all branch the same way. The refill card's visibility is data-driven
 * (shown whenever the renewal endpoint returns a medication), so the cohort only picks the
 * card variant.
 *
 * The attribute value is `{ prefersConciergeCare: boolean | null;
 * hasEmployerWithOptionalConciergeCare: boolean }` (matches PMP's `GET_CONCIERGE_CARE`).
 * `hasEmployerWithOptionalConciergeCare` only says whether the member *can* change the
 * setting, not what it currently is, so it's not read here. `prefersConciergeCare` is
 * opt-out, not opt-in: `false` means the member actively declined; `true` or `null` (never
 * declined) both mean "Yes" — so only an explicit `false` resolves to `OUTSIDE_9AM`.
 */
import { ProfileAttributesKeys } from "src/state/UserCubit/UserCubit";
import { userState } from "src/state/state";
import { RefillCohort } from "src/state/RefillRequirementsCubit/RefillRequirementsTypes";

const experienceGroup = (): string | undefined => {
  const value =
    userState.state.profileAttributes?.[ProfileAttributesKeys.experienceGroup];
  return typeof value === "string" ? value : undefined;
};

/** True when the member receives their GLP-1 through 9am (concierge care "Yes"). */
export const getsGlp1Through9am = (): boolean =>
  userState.state.profileAttributes?.[ProfileAttributesKeys.conciergeCare]
    ?.prefersConciergeCare !== false;

/**
 * True when the member's `experienceGroup` is an HWJ variant. Lets the decoupled refill
 * hub pick the payer-specific article URL without depending on the consistency score.
 */
export const isHwjMember = (): boolean =>
  experienceGroup()?.startsWith("HWJ_") ?? false;

/**
 * The member's refill cohort: `THROUGH_9AM` for concierge members (concierge care "Yes"),
 * `OUTSIDE_9AM` for everyone else. Only picks the card variant — visibility is data-driven.
 */
export const getRefillCohort = (): RefillCohort =>
  getsGlp1Through9am() ? RefillCohort.THROUGH_9AM : RefillCohort.OUTSIDE_9AM;
